import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { driverCardFor, getPricing, money } from './rateEngine';
import { manifestDay } from './payroll';
import { EmptyState, FlagBadge, Kpi, StatusBadge } from './shared';

/** Why a manifest can't be posted yet ({text, fix}), or null when it can. */
const postBlocker = (manifest, pricing, cards) => {
  if (manifest.status === 'posted') return { text: 'Already posted' };
  if (!manifest.driver?.id) return { text: 'Assign a driver first' };
  // With server pricing, a missing driver card shows up as driverRateMissing on every piece.
  if (!manifest.pricing && !driverCardFor(manifest, cards)) return { text: `${manifest.driver.name} has no driver rate card`, fix: 'rate-card' };
  if (pricing.driverRateMissing) return { text: `Driver rate card is missing rates for ${pricing.driverRateMissing} piece(s)' zones`, fix: 'rate-card' };
  if (pricing.openExceptions.length) return { text: `${pricing.openExceptions.length} exception(s) awaiting review`, fix: 'exceptions' };
  return null;
};

/**
 * Screen 1 — Daily Manifests
 * Upload the warehouse Route Sheet PDF, review parsed stops/pieces, see FSA match + revenue/driver pay,
 * assign the driver and post the manifest to the GL.
 */
const DailyManifests = ({ manifests, cards, drivers, day, onDayChange, focusId, onUpload, onAssignDriver, onPost, onOpenSettlement, onOpenExceptions, onOpenRateCard }) => {
  const [selectedId, setSelectedId] = useState(focusId);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (focusId) setSelectedId(focusId);
  }, [focusId]);

  const dayManifests = useMemo(
    () => manifests.filter((m) => manifestDay(m) === day).sort((a, b) => a.route.localeCompare(b.route)),
    [manifests, day]
  );
  const manifest = dayManifests.find((m) => m.id === selectedId) || dayManifests[0];
  const rated = useMemo(() => (manifest ? getPricing(manifest, cards) : null), [manifest, cards]);

  const rows = useMemo(() => {
    if (!rated) return [];
    const q = search.trim().toLowerCase();
    return rated.pieces.filter((p) => {
      if (filter === 'exceptions' && p.flags.length === 0) return false;
      if (filter === 'multi' && manifest.stops.find((s) => s.id === p.stopId)?.pieces.length < 2) return false;
      if (!q) return true;
      return [p.name, p.address, p.fsa, p.barcode].some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [rated, filter, search, manifest]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload the Route Sheet PDF');
      return;
    }
    setUploading(true);
    try {
      const created = await onUpload(file);
      if (created) setSelectedId(created.id);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePost = async () => {
    setPosting(true);
    try {
      await onPost(manifest);
    } finally {
      setPosting(false);
    }
  };

  const uploadButton = (
    <>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" id="manifest-upload-input" onChange={handleFile} />
      <Button id="btn-upload-manifest" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
        <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-file-upload'} mr-2`}></i>
        {uploading ? 'Parsing PDF…' : 'Upload Daily Manifest'}
      </Button>
    </>
  );

  const dayPicker = (
    <Input id="manifest-day" type="date" value={day} onChange={(e) => e.target.value && onDayChange(e.target.value)} className="w-[160px]" />
  );

  if (!manifest) {
    return (
      <div className="space-y-6" id="daily-manifests-screen">
        <div className="flex flex-wrap justify-end gap-2">
          {dayPicker}
          {uploadButton}
        </div>
        <EmptyState icon="fa-truck" title="No manifests for this day">
          Upload the warehouse Route Sheet PDF or pick another date.
        </EmptyState>
      </div>
    );
  }

  const t = rated.totals;
  const pieceMismatch = manifest.declared && manifest.declared.pieces !== t.pieces;
  const multiCount = manifest.stops.filter((s) => s.pieces.length > 1).length;
  const blocker = postBlocker(manifest, rated, cards);
  const locked = manifest.status === 'posted';
  const driverOptions = manifest.driver?.id && !drivers.some((d) => d.id === manifest.driver.id) ? [manifest.driver, ...drivers] : drivers;

  return (
    <div className="space-y-6" id="daily-manifests-screen">
      {/* Header / Route banner */}
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Route:</span>
                <h2 className="text-2xl font-bold font-mono">{manifest.route}</h2>
                <StatusBadge status={manifest.status} label={locked ? 'Posted to GL' : blocker ? 'Needs Review' : 'Ledger Ready'} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={t.matchRate === 1 ? 'border-emerald-500/40 text-emerald-500' : 'border-amber-500/40 text-amber-500'}>
                  <i className="fas fa-map-marker-alt mr-1"></i>
                  {(t.matchRate * 100).toFixed(0)}% FSA matched
                </Badge>
                <Badge variant="outline">{new Date(manifest.date).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}</Badge>
                <Badge variant="outline">{manifest.location}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Hub: <span className="text-foreground font-medium">{manifest.hub}</span> · Manifest Ref:{' '}
                <span className="font-mono text-foreground">{manifest.id}</span>
              </p>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="flex flex-wrap gap-2">
                {dayPicker}
                {uploadButton}
                <Button id="btn-open-settlement" variant="outline" onClick={() => onOpenSettlement(manifest)}>
                  <i className="fas fa-user-check mr-2"></i>Driver Settlement
                </Button>
                <Button id="btn-post-manifest" onClick={handlePost} disabled={!!blocker || posting} className="bg-sky-600 hover:bg-sky-700 text-white">
                  <i className={`fas ${posting ? 'fa-spinner fa-spin' : 'fa-book'} mr-2`}></i>
                  {locked ? 'Posted' : 'Post to GL'}
                </Button>
              </div>
              {blocker && !locked && (
                <button
                  id="manifest-post-blocker"
                  className="text-xs text-amber-500 hover:underline"
                  onClick={blocker.fix === 'exceptions' ? onOpenExceptions : blocker.fix === 'rate-card' ? onOpenRateCard : undefined}
                >
                  <i className="fas fa-info-circle mr-1"></i>
                  {blocker.text}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-12 gap-6">
        {/* Manifest list */}
        <Card className="col-span-12 lg:col-span-3 bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Manifests ({dayManifests.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dayManifests.map((m) => {
              const r = getPricing(m, cards);
              const active = m.id === manifest.id;
              return (
                <button
                  key={m.id}
                  id={`manifest-item-${m.id}`}
                  onClick={() => setSelectedId(m.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${active ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:border-muted-foreground/40 hover:bg-muted/40'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold text-sm">{m.route}</span>
                    <StatusBadge status={m.status} className="text-[10px]" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{m.driver?.name || 'Unassigned'}</p>
                  <div className="flex justify-between text-xs mt-2">
                    <span>{r.totals.pieces} pcs</span>
                    <span className="font-semibold">{money(r.totals.customer)}</span>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* KPIs + table */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Assigned Driver</p>
                  <i className="fas fa-id-badge text-muted-foreground"></i>
                </div>
                {locked ? (
                  <p className="text-2xl font-bold mt-2">{manifest.driver?.name || '—'}</p>
                ) : (
                  <Select
                    value={manifest.driver?.id || ''}
                    onValueChange={(id) => onAssignDriver(manifest, driverOptions.find((d) => d.id === id))}
                  >
                    <SelectTrigger id="manifest-driver-select" className={`mt-2 ${manifest.driver ? '' : 'border-amber-500/60'}`}>
                      <SelectValue placeholder="Assign driver…" />
                    </SelectTrigger>
                    <SelectContent>
                      {driverOptions.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground mt-1">{manifest.driver?.id || 'Required before posting'}</p>
              </CardContent>
            </Card>
            <Kpi
              label="Delivered Pieces"
              value={t.pieces}
              sub={`${t.stops} stops · ${t.weightLb.toFixed(2)} lb${pieceMismatch ? ` · ⚠ PDF says ${manifest.declared.pieces}` : ''}`}
              icon="fa-boxes"
              accent={pieceMismatch ? 'text-amber-500' : 'text-foreground'}
            />
            <Kpi label="Customer Billing" value={money(t.customer)} sub={`incl. ${money(t.accessorial)} accessorials`} icon="fa-file-invoice-dollar" accent="text-emerald-500" />
            <Kpi label="Driver Payout" value={money(t.driver)} sub={`Margin ${money(t.margin)} (${t.customer ? ((t.margin / t.customer) * 100).toFixed(1) : 0}%)`} icon="fa-wallet" />
          </div>

          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="relative flex-1 min-w-[240px]">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
                  <Input id="manifest-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by name, address, FSA or barcode…" className="pl-9" />
                </div>
                <div className="flex gap-2">
                  {[
                    ['all', `All Pieces (${t.pieces})`],
                    ['multi', `Multi-Piece Stops (${multiCount})`],
                    ['exceptions', `Exceptions (${rated.exceptions.length})`],
                  ].map(([key, label]) => (
                    <Button
                      key={key}
                      id={`manifest-filter-${key}`}
                      size="sm"
                      variant={filter === key ? 'default' : 'outline'}
                      className={filter === key && key === 'exceptions' ? 'bg-amber-500 hover:bg-amber-600 text-black' : filter === key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                      onClick={() => setFilter(key)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[520px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Sort</th>
                      <th className="text-left p-3">Recipient / Address</th>
                      <th className="text-left p-3">FSA</th>
                      <th className="text-center p-3">Zone</th>
                      <th className="text-left p-3">Barcode</th>
                      <th className="text-right p-3">Weight</th>
                      <th className="text-right p-3">Volume</th>
                      <th className="text-right p-3">Customer</th>
                      <th className="text-right p-3">Driver</th>
                      <th className="text-left p-3">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.barcode} className={`border-t border-border hover:bg-muted/30 ${p.flags.length && !p.review ? 'bg-amber-500/5' : ''}`}>
                        <td className="p-3 text-muted-foreground">{p.stopId}</td>
                        <td className="p-3">
                          <div className="font-medium capitalize">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.address}</div>
                        </td>
                        <td className="p-3 font-mono">{p.fsa || '—'}</td>
                        <td className="p-3 text-center">{p.zone ?? '—'}</td>
                        <td className="p-3 font-mono text-xs">{p.barcode}</td>
                        <td className="p-3 text-right">{p.weightLb.toFixed(2)} lb</td>
                        <td className="p-3 text-right text-xs">{p.volumeCm3.toLocaleString()} cm³</td>
                        <td className="p-3 text-right font-semibold">{money(p.customer)}</td>
                        <td className="p-3 text-right">{money(p.driver)}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {p.flags.map((f) => (
                              <FlagBadge key={f} flag={f} label={p.flagLabels?.[f]} />
                            ))}
                            {p.flags.length > 0 && p.review && (
                              <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-500">
                                <i className="fas fa-check mr-1"></i>
                                {p.review.decision}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-muted-foreground">No pieces match this filter.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DailyManifests;
